/**
 * BdrView — Decluttered (2026-05-18).
 *
 * Single focused work surface for BDRs. The mantra: phone in one hand,
 * queue + log + script on screen, nothing else competing for attention.
 *
 * Kept:
 *   - One thin KPI strip (Dials / Connects / Meetings + Cadence health dot)
 *   - QuickLogBar (one-tap channel logging)
 *   - Today's Queue table (the centerpiece)
 *   - Three inline resource cards (Cold call script, Marketing pain hooks, BDR manual)
 *
 * Removed:
 *   - ChannelSplitChart (eye candy, not actionable mid-call)
 *   - Cadence Health KPI card (folded into the strip)
 *   - Recent Activity right rail (he just did it, doesn't need the replay)
 *   - Just-Cleared Gates right rail (AE/manager signal, not BDR)
 *   - Activity Audit tab on dashboard (still reachable from sidebar)
 *   - Full OutreachLibrary panel (Templates is one click away in the sidebar)
 *
 * Legacy preserved at BdrView.predeclutter.tsx.bak.
 */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import OperatorTable, { StagePill, type OperatorColumn } from "@/components/operator/OperatorTable";
import { tokens } from "@/components/operator/tokens";
import { useAccounts, useBdrDailySurface, type BdrCadenceStep, type BdrCadenceTouch, type BdrHuntItem, type BdrProspectAccount } from "./live-data";
import { getActivityForWindow, type ActivityWindow } from "@/lib/activity-log-store";
import { getCurrentTenant, getSession } from "@/lib/session";
import QuickLogBar from "@/components/dial-and-log/QuickLogBar";
import DailyReconcileBoxV2 from "@/components/DailyReconcileBoxV2";
import OrphanContactAlert from "@/components/bdr/OrphanContactAlert";
import RepCoachingPanel from "@/components/coaching/RepCoachingPanel";
import {
  getChannelCounts,
  totalConversations,
  type ChannelCounts,
} from "@/lib/bdr-channel-store";

interface QueueRow {
  id: number;
  name: string;
  industry: string;
  stage: string;
}

// Tenant 2 (Mi6 Demo) sample queue only — never shown to real tenants.
const DEMO_QUEUE: QueueRow[] = [
  { id: 11, name: "Brightway Industrial",  industry: "Manufacturing",  stage: "Prospecting" },
  { id: 12, name: "Stratus Software",      industry: "SaaS",           stage: "Evaluating" },
  { id: 13, name: "Harborline Metals",     industry: "Metals & Mining", stage: "Prospecting" },
  { id: 14, name: "Ridgepoint Insurance",  industry: "Insurance",       stage: "Evaluating" },
  { id: 15, name: "Polaris Foods",         industry: "F&B",             stage: "Prospecting" },
];

// Daily anchor: 60 dials/day for the BDR daily framework.
const DIALS_DAILY_TARGET = 60;

export default function BdrView() {
  const [, setLocation] = useLocation();
  const accountsRes = useAccounts();
  const [activityWindow] = useState<ActivityWindow>("today");
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const session = getSession();
  const userId = session?.id ?? "current_user";

  const activity = useMemo(() => {
    try {
      return getActivityForWindow("bdr", activityWindow, { allowMockBaseline: isDemo });
    } catch {
      return { dials: 0, connects: 0, meetings: 0 };
    }
  }, [activityWindow, isDemo]);

  const [channelCounts, setChannelCounts] = useState<ChannelCounts>(() =>
    getChannelCounts(userId),
  );

  useEffect(() => {
    const refresh = () => setChannelCounts(getChannelCounts(userId));
    window.addEventListener("aip:bdr-channel-logged", refresh as EventListener);
    return () => {
      window.removeEventListener("aip:bdr-channel-logged", refresh as EventListener);
    };
  }, [userId]);

  // Counts: combine legacy activity-log values with quick-log taps so the
  // strip updates immediately when a BDR logs a dial.
  const dials = (activity.dials || 0) + channelCounts.phone_dial + (isDemo && !activity.dials ? 12 : 0);
  const connects = (activity.connects || 0) + totalConversations(channelCounts) + (isDemo && !activity.connects ? 3 : 0);
  const meetings = (activity.meetings || 0) + channelCounts.meeting_set + (isDemo && !activity.meetings ? 1 : 0);

  // Cadence health: total taps today across all channels. Plain text + dot.
  const taps =
    (channelCounts.phone_dial || 0) +
    (channelCounts.phone_connect || 0) +
    (channelCounts.email_send || 0) +
    (channelCounts.email_reply || 0) +
    (channelCounts.linkedin_msg || 0) +
    (channelCounts.linkedin_connect || 0) +
    (channelCounts.sms || 0) +
    (channelCounts.in_person || 0) +
    (channelCounts.meeting_set || 0);
  const cadenceLabel = taps === 0 ? "Red — start dialing" : taps < 20 ? `Orange — ${taps} taps` : `Green — ${taps} taps`;
  const cadenceColor = taps === 0 ? tokens.color.red : taps < 20 ? tokens.color.amber : tokens.color.success;

  // Queue rows from live accounts; demo fallback only for tenant 2.
  const liveRows: QueueRow[] = useMemo(() => {
    const accs = accountsRes.data ?? [];
    return accs.slice(0, 12).map((a) => {
      const raw = (a.stage || "").toLowerCase();
      const norm =
        /closing|won|commit/.test(raw) ? "Closing" :
        /propos/.test(raw) ? "Proposal" :
        /qualified|eval/.test(raw) ? "Evaluating" :
        /stall|omit|lost/.test(raw) ? "Stalled" :
        "Prospecting";
      return {
        id: a.id,
        name: a.name,
        industry: a.industry || "—",
        stage: norm,
      };
    });
  }, [accountsRes.data]);

  const rows = liveRows.length > 0 ? liveRows : (isDemo ? DEMO_QUEUE : []);
  const usingDemo = liveRows.length === 0 && isDemo;

  // W13: BDR daily surface live data
  const bdrSurface = useBdrDailySurface();

  const columns: OperatorColumn<QueueRow>[] = [
    {
      key: "name",
      header: "Account",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: tokens.color.textPrimary, fontSize: 14 }}>{r.name}</div>
          <div style={{ fontSize: 12, color: tokens.color.textMuted, marginTop: 2 }}>{r.industry}</div>
        </div>
      ),
    },
    { key: "stage", header: "Stage", render: (r) => <StagePill stage={r.stage} /> },
  ];

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Dashboards", href: "/dashboards" },
        { label: "BDR" },
      ]}
      pageTitle="BDR Dashboard"
      pageMeta={`Today · ${rows.length} contacts in queue${usingDemo ? " · sample data" : ""}`}
      primaryAction={{ label: "+ Add Expansion", href: "/accounts/new" }}
    >
      {/* LaneR: Rep coaching panel — above daily surface */}
      {session?.id && (
        <RepCoachingPanel repUserId={session.id} repRole="bdr" />
      )}

      {/* W10: Orphan contact alert — top of Daily Surface */}
      <OrphanContactAlert />

      {/* KPI strip — one line, no cards, no charts, no progress bars */}
      <div
        style={{
          display: "flex",
          gap: 32,
          padding: "10px 14px",
          background: tokens.color.surfaceMute,
          borderRadius: 8,
          marginBottom: 16,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <KpiInline label="Dials" value={`${dials} / ${DIALS_DAILY_TARGET}`} />
        <KpiInline label="Connects" value={String(connects)} />
        <KpiInline label="Meetings booked" value={String(meetings)} />
        <div style={{ marginLeft: "auto", fontSize: 12, color: tokens.color.textMuted }}>
          Cadence health:&nbsp;
          <span style={{ fontWeight: 700, color: cadenceColor }}>● {cadenceLabel}</span>
        </div>
      </div>

      {/* Quick log — one-tap buttons */}
      <SectionTitle>Log activity</SectionTitle>
      <div style={{ marginBottom: 18 }}>
        <QuickLogBar onChange={setChannelCounts} />
      </div>

      {/* Today's Queue — the centerpiece */}
      <SectionTitle>{`Today's queue · ${rows.length} ${rows.length === 1 ? "contact" : "contacts"}`}</SectionTitle>
      <OperatorTable
        title=""
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        // 2026-05-20 fix: BDR queue clicks open the cadence work surface, not the
        // account scorecard. The scorecard blocks all tabs behind Gate 0 which
        // is the wrong UX for prospecting — BDRs need contact + cadence + log,
        // not a qualification wall.
        onRowClick={() => setLocation(`/cadence/bdr`)}
        emptyMessage="Queue is empty for today."
      />

      {/* W13: BDR daily surface — live Supabase data, no mock */}
      {!bdrSurface.loading && bdrSurface.data && (() => {
        const { cadence_steps, todays_touches, open_hunts, prospect_coverage } = bdrSurface.data;
        const CP_DOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        const cpColor = (status: string) => {
          if (status === "complete") return tokens.color.success;
          if (status === "in_progress") return "#EAB308";
          if (status === "blocked") return tokens.color.red;
          return tokens.color.border;
        };
        return (
          <>
            {/* Cadence queue — live cadence_enrollments due today or overdue */}
            {cadence_steps.length > 0 && (
              <>
                <SectionTitle style={{ marginTop: 22 }}>
                  {`Today's cadence queue \u00b7 ${cadence_steps.length} suggested ${cadence_steps.length === 1 ? "touch" : "touches"}`}
                </SectionTitle>
                <div style={{
                  background: tokens.color.canvas,
                  border: `1px solid ${tokens.color.border}`,
                  borderRadius: 8,
                  marginBottom: 12,
                  overflow: "hidden",
                }}>
                  {cadence_steps.map((step: BdrCadenceStep) => {
                    const isOverdue = step.next_action_at
                      ? step.next_action_at < new Date().toISOString().slice(0, 10)
                      : false;
                    const hasMarketingHook = !!step.marketing_hook_kind;
                    const borderColor = hasMarketingHook
                      ? "#B45309"
                      : isOverdue
                      ? tokens.color.red
                      : "transparent";
                    return (
                      <div key={step.id} style={{
                        padding: "10px 16px",
                        borderBottom: `1px solid ${tokens.color.border}`,
                        borderLeft: `2px solid ${borderColor}`,
                        fontSize: 13,
                        color: tokens.color.textPrimary,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}>
                        {/* Step dot ladder: 6 dots, filled up to current_step */}
                        <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                          {[1,2,3,4,5,6].map((n) => (
                            <div
                              key={n}
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: n <= step.current_step
                                  ? tokens.color.red
                                  : tokens.color.border,
                              }}
                            />
                          ))}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: tokens.color.textPrimary, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {step.contact_name || step.account_name || "Contact"}
                          </div>
                          {step.account_name && step.contact_name && (
                            <div style={{ fontSize: 11, color: tokens.color.textMuted, marginTop: 1 }}>
                              {step.account_name}
                            </div>
                          )}
                        </div>
                        <span style={{
                          background: tokens.color.border,
                          borderRadius: 4,
                          padding: "2px 6px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: tokens.color.textMuted,
                          whiteSpace: "nowrap",
                        }}>
                          {`Step ${step.current_step} of 6`}
                        </span>
                        {step.next_action_at && (
                          <span style={{
                            fontSize: 11,
                            color: isOverdue ? tokens.color.red : tokens.color.textMuted,
                            whiteSpace: "nowrap",
                          }}>
                            {isOverdue ? "Overdue" : step.next_action_at.slice(0, 10)}
                          </span>
                        )}
                        {hasMarketingHook && (
                          <span style={{
                            fontSize: 10,
                            color: "#B45309",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}>
                            Marketing signal
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <SectionTitle style={{ marginTop: cadence_steps.length > 0 ? 16 : 22 }}>Today's touches</SectionTitle>
            <div style={{
              background: tokens.color.canvas,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: 8,
              marginBottom: 12,
              overflow: "hidden",
            }}>
              {todays_touches.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 13, color: tokens.color.textMuted }}>
                  No open touches for today.
                </div>
              ) : (
                todays_touches.map((touch: BdrCadenceTouch) => (
                  <div key={touch.id} style={{
                    padding: "10px 16px",
                    borderBottom: `1px solid ${tokens.color.border}`,
                    fontSize: 13,
                    color: tokens.color.textPrimary,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}>
                    <span style={{
                      background: tokens.color.border,
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: tokens.color.textMuted,
                      whiteSpace: "nowrap",
                    }}>{touch.action_kind}</span>
                    <span style={{ flex: 1, color: tokens.color.textMuted, fontSize: 12 }}>
                      {touch.notes ?? "No notes"}
                    </span>
                    <span style={{ fontSize: 11, color: tokens.color.textMuted }}>
                      {touch.executed_at ? touch.executed_at.slice(11, 16) : ""}
                    </span>
                  </div>
                ))
              )}
            </div>

            <SectionTitle style={{ marginTop: 16 }}>Open hunts</SectionTitle>
            <div style={{
              background: tokens.color.canvas,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: 8,
              marginBottom: 12,
              overflow: "hidden",
            }}>
              {open_hunts.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 13, color: tokens.color.textMuted }}>
                  No open hunts.
                </div>
              ) : (
                open_hunts.map((hunt: BdrHuntItem) => (
                  <div key={hunt.id} style={{
                    padding: "10px 16px",
                    borderBottom: `1px solid ${tokens.color.border}`,
                    fontSize: 13,
                    color: tokens.color.textPrimary,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}>
                    <span style={{
                      background: tokens.color.border,
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: tokens.color.textMuted,
                      whiteSpace: "nowrap",
                    }}>{hunt.item_key}</span>
                    <span style={{ flex: 1, fontWeight: 500, color: tokens.color.textPrimary, fontSize: 13 }}>
                      {hunt.label}
                    </span>
                    {hunt.due_date && (
                      <span style={{ fontSize: 11, color: tokens.color.textMuted, whiteSpace: "nowrap" }}>
                        {hunt.due_date}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>

            <SectionTitle style={{ marginTop: 16 }}>Your prospect coverage</SectionTitle>
            <div style={{
              background: tokens.color.canvas,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: 8,
              marginBottom: 12,
              overflow: "hidden",
            }}>
              {prospect_coverage.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 13, color: tokens.color.textMuted }}>
                  No prospects assigned to you.
                </div>
              ) : (
                prospect_coverage.map((acct: BdrProspectAccount) => {
                  const statusMap: Record<number, string> = {};
                  for (const cs of acct.account_cp_status) statusMap[cs.cp_number] = cs.status;
                  const completedCount = Object.values(statusMap).filter(s => s === "complete").length;
                  return (
                    <div key={acct.id} style={{
                      padding: "10px 16px",
                      borderBottom: `1px solid ${tokens.color.border}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}>
                      <span style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: tokens.color.textPrimary,
                        minWidth: 140,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>{acct.name}</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        {CP_DOTS.map((cp) => (
                          <div
                            key={cp}
                            title={`CP${cp}: ${statusMap[cp] ?? "none"}`}
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: cpColor(statusMap[cp] ?? ""),
                              border: `1px solid ${tokens.color.border}`,
                            }}
                          />
                        ))}
                      </div>
                      <span style={{ fontSize: 11, color: completedCount > 0 ? (tokens.color.success) : tokens.color.textMuted, fontWeight: 600 }}>
                        {completedCount}/{CP_DOTS.length}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </>
        );
      })()}
      {bdrSurface.error && (
        <div style={{
          marginBottom: 12,
          padding: "10px 14px",
          borderRadius: 8,
          background: "#FFF5F5",
          border: `1px solid ${tokens.color.red}`,
          fontSize: 12,
          color: tokens.color.textMuted,
        }}>
          Live surface data unavailable. Check your connection.
        </div>
      )}

      {/* End-of-day reconciliation — V2 side-by-side widget */}
      <SectionTitle style={{ marginTop: 22 }}>End of day</SectionTitle>
      <div style={{ marginBottom: 18 }}>
        <DailyReconcileBoxV2 />
      </div>

      {/* Resources for today — inline cards, BDR-relevant only */}
      <SectionTitle style={{ marginTop: 22 }}>Resources for today</SectionTitle>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
        }}
      >
        <ResourceCard
          name="Cold call script"
          desc="For first-touch CP1 contacts"
          cta="Open template"
          onClick={() => setLocation("/templates")}
        />
        <ResourceCard
          name="Marketing pain hooks"
          desc="Talking points by industry"
          cta="View hooks"
          onClick={() => setLocation("/marketing")}
        />
        <ResourceCard
          name="BDR manual"
          desc="Daily framework + cadence rules"
          cta="Open manual"
          onClick={() => setLocation("/manuals")}
        />
      </div>
    </OperatorShell>
  );
}

// ---------------------------------------------------------------------------
// Local helper components — keeps the file self-contained.
// ---------------------------------------------------------------------------

function KpiInline({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.06em",
          color: tokens.color.textMuted,
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: tokens.font.display,
          fontWeight: 700,
          fontSize: 20,
          color: tokens.color.textPrimary,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        fontFamily: tokens.font.display,
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: tokens.color.textMuted,
        margin: "0 0 12px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function ResourceCard({
  name,
  desc,
  cta,
  onClick,
}: {
  name: string;
  desc: string;
  cta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 12,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: 6,
        background: tokens.color.canvas,
        cursor: "pointer",
        fontFamily: tokens.font.body,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, color: tokens.color.textPrimary, marginBottom: 4 }}>
        {name}
      </div>
      <div style={{ fontSize: 12, color: tokens.color.textMuted, marginBottom: 8 }}>{desc}</div>
      <div style={{ color: tokens.color.red, fontSize: 12, fontWeight: 600 }}>{cta} →</div>
    </button>
  );
}
